/**
 * CORE EVENTS SYSTEM - Gestione Eventi Centralizzata
 * Sistema di eventi ottimizzato per FantAPP
 */

class EventManager {
  constructor() {
    this.listeners = new Map();
    this.debounceTimers = new Map();
    /** Cache liste ordinate per priorità — invalidata su on/off. */
    this._sortedByEvent = new Map();
    // ID univoco per debug istanze
    this._id = `EM-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  _invalidateSorted(event) {
    this._sortedByEvent.delete(event);
  }

  /**
   * Registra un listener per un evento
   */
  on(event, callback, options = {}) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    
    const listener = {
      callback,
      once: options.once || false,
      debounce: options.debounce || 0,
      priority: options.priority || 0
    };
    
    this.listeners.get(event).add(listener);
    this._invalidateSorted(event);
  }

  /**
   * Rimuove un listener
   */
  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const eventListeners = this.listeners.get(event);
    for (const listener of eventListeners) {
      if (listener.callback === callback) {
        eventListeners.delete(listener);
        this._invalidateSorted(event);
        break;
      }
    }
  }

  /**
   * Emette un evento (con debounce ottimizzato)
   */
  emit(event, data) {
    console.log(`[EventManager ${this._id}] Emettendo evento '${event}' con ${this.listeners.has(event) ? this.listeners.get(event).size : 0} listener(s)`);
    
    if (!this.listeners.has(event)) {
      console.log(`[EventManager ${this._id}] Nessun listener per '${event}'`);
      return;
    }

    let eventListeners = this._sortedByEvent.get(event);
    if (!eventListeners) {
      eventListeners = Array.from(this.listeners.get(event))
        .sort((a, b) => b.priority - a.priority);
      this._sortedByEvent.set(event, eventListeners);
    }
    
    console.log(`[EventManager ${this._id}] Eseguendo ${eventListeners.length} listener per '${event}'`);

    let removedOnce = false;
    for (const listener of eventListeners) {
      if (listener.debounce > 0) {
        // Debounce
        const timerId = `${event}-${listener.callback.name}`;
        clearTimeout(this.debounceTimers.get(timerId));
        this.debounceTimers.set(timerId, setTimeout(() => {
          this.executeListener(listener, data);
          this.debounceTimers.delete(timerId);
        }, listener.debounce));
      } else {
        // Immediato
        this.executeListener(listener, data);
      }

      // Rimuovi se once
      if (listener.once) {
        this.listeners.get(event).delete(listener);
        removedOnce = true;
      }
    }
    if (removedOnce) this._invalidateSorted(event);
  }

  /**
   * Esegue un listener in modo sicuro
   */
  executeListener(listener, data) {
    try {
      listener.callback(data);
    } catch (error) {
      console.error('[Events] Errore nel listener:', error);
    }
  }

  /**
   * Pulisce tutti i timer attivi
   */
  cleanup() {
    for (const [timerId, timer] of this.debounceTimers) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
    this.listeners.clear();
    this._sortedByEvent.clear();
  }
}

// Singleton globale resiliente a duplicati di import
const g = (typeof window !== 'undefined') ? window : globalThis;
export const eventManager = g.__FANTAPP_EVENT_MANAGER__ || (g.__FANTAPP_EVENT_MANAGER__ = new EventManager());

// Helper per eventi comuni
export const Events = {
  // Tabella
  TABLE_RENDER: 'table:render',
  TABLE_UPDATE: 'table:update',
  TABLE_SORT: 'table:sort',
  
  // Filtri
  FILTERS_CHANGE: 'filters:change',
  FILTERS_RESET: 'filters:reset',
  
  // Giocatori
  PLAYER_SELECT: 'player:select',
  PLAYER_ASSIGN: 'player:assign',
  PLAYER_FAVORITE: 'player:favorite',
  
  // UI
  UI_LOADING: 'ui:loading',
  UI_ERROR: 'ui:error',
  UI_SUCCESS: 'ui:success',
};
